import { CSSProperties } from "react";
import ProgressiveImage from "./ProgressiveImage";

interface Props {
  src: string;
  alt: string;
  height?: number;
  width?: number;
  objectFit?: "contain" | "cover" | "fill";
  layout?: string; // "fixed" | "fill" | "intrinsic" | "responsive" | "raw";
  onClick?: () => void;
  /** Fires once the bitmap is decoded — use it to reveal an image only when it can actually paint. */
  onLoad?: () => void;
  className?: string;
  priority?: boolean;
  /**
   * Overrides the `priority`-derived default. Needed for images that are MOUNTED on
   * demand (product-card slider slides): they only exist once the shopper is already
   * looking at them, so `lazy` would make the browser defer the very image just
   * swiped to. Leave unset to keep the `priority ? eager : lazy` default.
   */
  loading?: "eager" | "lazy";
  sizes?: string;
  style?: CSSProperties;
  quality?: number;
  noPlaceholder?: boolean;
  containerStyle?: CSSProperties;
  containerClassName?: string;
}
const sanitizeSrc = (srcLink: string) => {
  if (!srcLink) return "/";
  if (
    !(
      srcLink.startsWith("https://") ||
      srcLink.startsWith("http://") ||
      srcLink.startsWith("/")
    )
  ) {
    return "/" + srcLink;
  }
  return srcLink;
};

const NextImage: React.FC<Props> = ({
  src,
  alt,
  height,
  width,
  objectFit,
  layout,
  className = null,
  priority = false,
  loading,
  sizes,
  style,
  quality = 75,
  noPlaceholder = false,
  containerStyle,
  containerClassName,
  onClick,
  onLoad,
}) => {
  const isSmall = !!width && width < 50;
  const shouldProgressive = !isSmall && !noPlaceholder;

  const fill = !(!!width && !!height) && !style?.width;
  const imgStyle: any = {
    objectFit,
    objectPosition: "50% 50%",
    layout: (!!width && !!height) || style?.width ? layout : "fill",
    ...style,
  };

  return className ? (
    <ProgressiveImage
      disableEffect={!shouldProgressive}
      alt={alt}
      src={sanitizeSrc(src)}
      priority={priority}
      onClick={onClick}
      onLoad={onLoad}
      fill={fill}
      width={width}
      height={height}
      style={imgStyle}
      className={className}
      sizes={sizes}
      quality={quality}
      loading={loading ?? (priority ? "eager" : "lazy")}
    />
  ) : (
    <div
      className={"next-image-container " + containerClassName}
      style={{ ...containerStyle, overflow: "hidden" }}
    >
      <ProgressiveImage
        disableEffect={!shouldProgressive}
        alt={alt}
        onClick={onClick}
      onLoad={onLoad}
        fill={fill}
        width={width}
        height={height}
        style={imgStyle}
        src={sanitizeSrc(src)}
        sizes={sizes}
        quality={quality}
        priority={priority}
        loading={loading ?? (priority ? "eager" : "lazy")}
      />
    </div>
  );
};

export default NextImage;
